import settings from '@/constants/constants';
import { FF_ENABLE_IMMEDIATE_CONSULTS_PROMO_CODES, FF_ENABLE_INC_VIDEO_CALL } from '@/constants/featureFlags';
import {
  IMMEDIATE_NURSE_CONSULT_SMS_LOCAL_STORAGE_KEY,
  IMMEDIATE_NURSE_CONSULT_TRACKING_ID,
} from '@/constants/immediateNurseConsultation';
import { GoogleAnalyticsEventName } from '@/constants/types';
import { usePrescreeningData } from '@/context/pre-screening-context';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import useGoogleTagManager from '@/hooks/useGoogleTagManager';
import {
  ImmediateNurseConsultationVariant,
  useImmediateNurseConsultVariant,
} from '@/hooks/useImmediateNurseConsultVariant';
import { useInstantConsultRequest } from '@/hooks/useInstantConsultRequest';
import { useSmsVerification } from '@/hooks/useSmsVerification';
import type { FormValues } from '@/pages/components/PersonalDetailForms';
import { PersonalDetailFormWithoutCallType, PersonalDetailsForm } from '@/pages/components/PersonalDetailForms';
import { formatStringToSha256Hash } from '@/utils/ga-hashing';
import { getRateLimitIndexByTimeout } from '@/utils/immediate-nurse-consult';
import { getSearchParams } from '@/utils/url';
import { Stack } from '@montugroup/design-system';
import { useFormBuilder } from '@montugroup/form-builder';
import { Logger } from '@montugroup/utilities';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

type CallType = 'VOICE' | 'VIDEO';

export const CALL_TYPE_SERVER: Record<CallType, CallType> = {
  VOICE: 'VOICE',
  VIDEO: 'VIDEO',
};

// Mapping call types to Proper case on request by Data team
export const CALL_TYPE_ANALYTICS: Record<CallType, string> = {
  VOICE: 'Voice',
  VIDEO: 'Video',
};

const logger = new Logger('ContactNurse');

export const ContactNurse = () => {
  const { sendGoogleAnalyticsEvent } = useGoogleTagManager();
  const { preScreeningData, setPrescreeningData } = usePrescreeningData();
  const [smsConfirmationDialogOpen, setSmsConfirmationDialogOpen] = useState(false);
  const { sendInstantConsultRequest } = useInstantConsultRequest();
  const { flags } = useFeatureFlags();
  const isIncVideoCallEnabled = flags[FF_ENABLE_INC_VIDEO_CALL];

  const { setCurrentStepIndex } = useFormBuilder();

  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    trigger,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      callType: undefined,
    },
  });

  const handleOnNextClick = () => {
    setCurrentStepIndex((prev) => prev + 1);
  };

  const immediateConsultVariantFromLd = useImmediateNurseConsultVariant();
  const immediateConsultVariant = preScreeningData?.immediateNurseConsultation.variant ?? immediateConsultVariantFromLd;
  const [submissionError, setSubmissionError] = useState(false);

  const { startSmsVerificationCountdown } = useSmsVerification();

  const isPromoCodeEnabled = flags[FF_ENABLE_IMMEDIATE_CONSULTS_PROMO_CODES];
  const userEmail = getSearchParams('email', { localStorageKey: 'prescreeningUserEmail' });
  const promoCode = isPromoCodeEnabled ? getSearchParams('promocode') : null;

  const shaEmail = userEmail ? formatStringToSha256Hash(userEmail) : '';

  const onPhoneNumberConfirmationDialogClose = (trackEvent: boolean) => {
    if (trackEvent) {
      const trackingPayload = {
        event_category: 'cal.com',
        event_label: 'Immediate Nurse Consultation',
        sha_email: shaEmail,
        variant_id: immediateConsultVariant,
      };
      sendGoogleAnalyticsEvent?.(GoogleAnalyticsEventName.START_BOOKING, trackingPayload);
    }
    setSmsConfirmationDialogOpen(false);
  };

  const onStartConsultation = async (data: FormValues) => {
    try {
      if (immediateConsultVariant === ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC) {
        handleOnNextClick();
      }

      setSubmissionError(false);

      const responseBody = await sendInstantConsultRequest({
        ...(immediateConsultVariant === ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC && {
          variant: 'variant-amazon-connect-initiated-inc' as const,
        }),
        attributes: {
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          email: userEmail ?? '',
          ...(data.callType ? { callType: data.callType } : {}),
          ...(promoCode ? { promoCode } : {}),
          ...(isIncVideoCallEnabled ? { callType: CALL_TYPE_SERVER.VIDEO } : {}),
          intakeVariant: 'INC' as const,
        },
      });

      const trackingPayload = {
        event_category: 'cal.com',
        event_label: 'Immediate Nurse Consultation',
        sha_email: shaEmail,
        sha_gads_phone_number: formatStringToSha256Hash(data.phone),
        sha_fb_phone_number: formatStringToSha256Hash(data.phone),
        sha_first_name: formatStringToSha256Hash(data.firstName),
        sha_last_name: formatStringToSha256Hash(data.lastName),
        ...(isIncVideoCallEnabled && { call_type: CALL_TYPE_ANALYTICS['VIDEO'] }),
      };

      sendGoogleAnalyticsEvent?.(GoogleAnalyticsEventName.SUBMIT_BOOKING, trackingPayload);

      localStorage.setItem(IMMEDIATE_NURSE_CONSULT_TRACKING_ID, JSON.stringify(trackingPayload));

      const retryAfterMs = responseBody?.data?.retryAfterMs;

      if (immediateConsultVariant === ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC) {
        setPrescreeningData((current) => {
          return {
            ...current,
            patientDetails: {
              firstName: data.firstName ?? '',
              lastName: data.lastName ?? '',
              phone: data.phone ?? '',
              email: userEmail ?? '',
            },
            smsVerification: {
              rateLimitIndex: getRateLimitIndexByTimeout(retryAfterMs || 0),
            },
          };
        });

        localStorage.removeItem(IMMEDIATE_NURSE_CONSULT_SMS_LOCAL_STORAGE_KEY);

        startSmsVerificationCountdown(retryAfterMs);
      } else {
        if (settings.instantConnectPhoneNumber) {
          window.location.href = `tel:${settings.instantConnectPhoneNumber}`;
          handleOnNextClick();
        }
      }
    } catch (error) {
      logger.error('An error occurred while setting up the immediate consultation: ', error);
      setSubmissionError(true);
    }
  };

  const onVerifyPhoneNumber = async (data: FormValues) => {
    setPrescreeningData((current) => {
      return {
        ...current,
        patientDetails: {
          firstName: data.firstName ?? '',
          lastName: data.lastName ?? '',
          phone: data.phone ?? '',
          email: userEmail ?? '',
          callType: data.callType,
        },
        smsVerification: {
          rateLimitIndex: 0,
        },
      };
    });

    setSmsConfirmationDialogOpen(true);
  };

  const showScheduleForLaterButton: boolean = [
    ImmediateNurseConsultationVariant.CONTROL,
    ImmediateNurseConsultationVariant.VARIANT,
  ].includes(immediateConsultVariant);

  const onSubmitForm = async (data: FormValues) => {
    if (immediateConsultVariant === ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC) {
      await onVerifyPhoneNumber(data);
    } else {
      await onStartConsultation(data);
    }
  };

  return (
    <Stack sx={{ minHeight: '70vh' }}>
      {isIncVideoCallEnabled ? (
        <PersonalDetailsForm
          onSubmit={handleSubmit(onSubmitForm)}
          submissionError={submissionError}
          control={control}
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          trigger={trigger}
          showScheduleForLaterButton={showScheduleForLaterButton}
          smsConfirmationDialogOpen={smsConfirmationDialogOpen}
          onPhoneNumberConfirmationDialogClose={onPhoneNumberConfirmationDialogClose}
          onStartConsultation={onStartConsultation}
        />
      ) : (
        <PersonalDetailFormWithoutCallType
          onSubmit={handleSubmit(onSubmitForm)}
          immediateConsultVariant={immediateConsultVariant}
          submissionError={submissionError}
          control={control}
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          showScheduleForLaterButton={showScheduleForLaterButton}
          smsConfirmationDialogOpen={smsConfirmationDialogOpen}
          onPhoneNumberConfirmationDialogClose={onPhoneNumberConfirmationDialogClose}
          onStartConsultation={onStartConsultation}
        />
      )}
    </Stack>
  );
};
